/**
 * CheckInStepper (BON-204 R2) — the party>1 check-in bottom-sheet. Holds a LOCAL
 * count (seeded from the booking) that the −/＋ buttons and the "All N here"
 * shortcut adjust without any network calls; the operator decision is to commit
 * exactly ONCE, on Confirm, with the final count (`onConfirm(count)`). Closing
 * (× / Escape / scrim / Confirm-then-close) is the parent's job.
 *
 * Mobile-friendly: a sheet pinned to the bottom of the viewport, full-width
 * controls, large tap targets. Same scrim/dialog chrome as the modal it lives
 * under — but anchored low so a thumb can reach the stepper on a phone.
 */
import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { RosterBooking } from '../../../../api/workshops';

import { displayName } from './checkInModel';

export interface CheckInStepperProps {
  booking: RosterBooking;
  /** True while the confirm mutation is in flight — disables the controls. */
  pending: boolean;
  /** Commit the final count (one PUT). */
  onConfirm: (count: number) => void;
  onClose: () => void;
}

export function CheckInStepper({ booking, pending, onConfirm, onClose }: CheckInStepperProps) {
  const max = booking.attendeeCount;
  const [count, setCount] = useState(Math.min(booking.checkedInCount, max));
  const atMax = count >= max;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dec = () => setCount((c) => Math.max(0, c - 1));
  const inc = () => setCount((c) => Math.min(max, c + 1));

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 70,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Check in ${displayName(booking)}`}
        onClick={(e) => e.stopPropagation()}
        className="nb-card"
        style={{
          width: 460,
          maxWidth: '100%',
          margin: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          borderRadius: 18,
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--nb-ink)' }}>
          {displayName(booking)} · Party of {max} · how many are here now?
        </div>
        <Controls count={count} max={max} pending={pending} atMax={atMax} onDec={dec} onInc={inc} />
        <Actions
          max={max}
          atMax={atMax}
          pending={pending}
          onAll={() => setCount(max)}
          onConfirm={() => onConfirm(count)}
        />
      </div>
    </div>
  );
}

/** The −/count/＋ row with the "{count} checked in" + remaining subtext. */
function Controls({
  count,
  max,
  pending,
  atMax,
  onDec,
  onInc,
}: {
  count: number;
  max: number;
  pending: boolean;
  atMax: boolean;
  onDec: () => void;
  onInc: () => void;
}) {
  const subtext = atMax ? "Everyone's here 🎉" : `${max - count} not here yet`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22 }}>
      <StepButton label="decrement" disabled={pending || count === 0} onClick={onDec}>
        <Minus size={20} strokeWidth={3} aria-hidden="true" />
      </StepButton>
      <div style={{ textAlign: 'center', minWidth: 72 }}>
        <div
          data-testid="stepper-count"
          style={{ fontSize: 34, fontWeight: 700, color: 'var(--nb-ink)', lineHeight: 1 }}
        >
          {count}
          <span style={{ fontSize: 18, fontWeight: 500, color: 'var(--nb-subtle)' }}> / {max}</span>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)', marginTop: 6 }}>
          {count} checked in
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-primary)', marginTop: 2 }}>{subtext}</div>
      </div>
      <StepButton label="increment" disabled={pending || atMax} onClick={onInc}>
        <Plus size={20} strokeWidth={3} aria-hidden="true" />
      </StepButton>
    </div>
  );
}

/** "All N here" shortcut (hidden at max) + the Confirm button. */
function Actions({
  max,
  atMax,
  pending,
  onAll,
  onConfirm,
}: {
  max: number;
  atMax: boolean;
  pending: boolean;
  onAll: () => void;
  onConfirm: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {!atMax && (
        <button
          type="button"
          className="nb-btn nb-btn-ghost"
          disabled={pending}
          onClick={onAll}
          style={{ flex: '0 0 auto' }}
        >
          All {max} here
        </button>
      )}
      <button
        type="button"
        className="nb-btn nb-btn-primary"
        disabled={pending}
        onClick={onConfirm}
        style={{
          flex: 1,
          background: 'var(--nb-primary)',
          borderColor: 'var(--nb-primary)',
          color: '#fff',
        }}
      >
        Confirm
      </button>
    </div>
  );
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 48,
        height: 48,
        borderRadius: 999,
        border: '1.5px solid var(--nb-border-2)',
        background: 'var(--nb-bg)',
        color: 'var(--nb-ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
